import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import Animation from "@/Shared/Animations/Animation";
import CreditCardsCardEntryForm from "@/Shared/CreditCards/Forms/CreditCardsCardEntryForm";
import useStoreCreditCard from "@/Features/CreditCards/Exports/Hooks/useCreditCardsStore";
import { useOnce } from "@/Hooks/useOnce";
import useQuery from "@/Hooks/useQuery";
import { useEffect, useState } from "react";
import { useIntercom } from "react-use-intercom";
import CreditCardsAddLegalease from "@/Shared/CreditCards/CustomComponents/CreditCardsAddLegalease";
import { CreditCardsFormPane } from "./CustomComponents/Internal/CreditCardsFormPane";
import CreditCardsStoreButton from "@/Shared/CreditCards/Buttons/CreditCardsStoreButton";

function ExternalAddCreditCardScreen() {
  const {
    storeCreditCard,
    billingAddress,
    billingName,
    setBillingName,
    setBillingAddress,
    errorMessageForBillingName,
    errorMessageForCreditCardNumber,
    errorMessageForCreditCardExpiration,
    errorMessageForCreditCardCVC,
    setErrorMessageForCreditCardNumber,
    setErrorMessageForCreditCardExpiration,
    setErrorMessageForCreditCardCVC,
    errorMessageForAddress,
    errorMessageForCity,
    errorMessageForStateOrProvinceCode,
    locationsErrorMessageForPostalCode,
    errorMessageForCountry,
    isValid,
  } = useStoreCreditCard();

  const { boot } = useIntercom();

  useEffect(() => {
    boot();
  });

  const [storingInProgress, setStoringInProgress] = useState(false);
  const [storingSuccess, setStoringSuccess] = useState(false);
  const [storingFailure, setStoringFailure] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string>();
  const [company, setCompany] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [validated, setValidated] = useState(false);

  const paymentMethodsApi = usePaymentMethodsApi();

  const query = useQuery();

  const [loading, setLoading] = useState(true);

  useOnce(async () => {
    const idParam = query["id"];
    const saveCardIntentResponse =
      await paymentMethodsApi.swapAddCreditCardAuthorizationIdForSaveCardIntent(
        {
          addCreditCardAuthorizationId:
            typeof idParam === "string" ? idParam : "",
        },
      );
    setCompany(saveCardIntentResponse.companyName);
    setClientSecret(saveCardIntentResponse.clientSecret);
    setLoading(false);
  });

  async function onStoreCreditCard() {
    setValidated(true);

    if (!isValid) {
      return;
    }

    setStoringSuccess(false);
    setStoringFailure(false);
    setStoringInProgress(true);

    const result = await storeCreditCard(clientSecret);

    setStoringInProgress(false);

    if (result.setupIntent) {
      setStoringSuccess(true);
    } else {
      setStoringFailure(true);
      setFailureMessage(result.error.message);
    }
  }

  const formVisible = !storingInProgress && !storingSuccess;

  if (loading) {
    return <></>;
  }

  // Customer-facing, so underlined like the rest of the customer flows.
  return (
    <ControlPresentationProvider presentation="underlined">
      <Stack align="center" gap={8}>
        <Logo />
        {storingInProgress && (
          <Stack align="center" gap={4}>
            <Heading level={3}>
              Securely storing credit card information
            </Heading>
            <Spinner size={32} />
          </Stack>
        )}
        {storingSuccess && (
          <Stack align="center" gap={6}>
            <Heading level={3}>Credit Card Securely Stored!</Heading>
            <Animation
              pathToAnimation="/animations/credit-card-success.json"
              lottieFilesUrl="https://lottiefiles.com/8311-loading"
              loop
              width={200}
            />
            <T size="sm" color="secondary">
              Your colleague can now book shipments on FreightSimple. You can
              now close this tab.
            </T>
          </Stack>
        )}
        <CreditCardsFormPane visible={formVisible}>
          <Stack gap={4}>
            <Stack gap={1}>
              <Heading level={2}>Enter Credit Card Details</Heading>
              <T size="sm" color="secondary">
                Someone has requested that you provide credit card details for{" "}
                <strong>{company}</strong>.
              </T>
              <T size="sm" color="secondary">
                Payment information is required in order to book the shipment.
              </T>
            </Stack>
            <CreditCardsCardEntryForm
              addresses={[]}
              billingAddress={billingAddress}
              billingName={billingName}
              setBillingAddress={setBillingAddress}
              setBillingName={setBillingName}
              forceValidation={validated}
              errorMessageForBillingName={errorMessageForBillingName}
              errorMessageForCreditCardNumber={errorMessageForCreditCardNumber}
              errorMessageForCreditCardExpiration={
                errorMessageForCreditCardExpiration
              }
              errorMessageForCreditCardCVC={errorMessageForCreditCardCVC}
              errorMessageForAddress={errorMessageForAddress}
              errorMessageForCity={errorMessageForCity}
              errorMessageForStateOrProvinceCode={
                errorMessageForStateOrProvinceCode
              }
              locationsErrorMessageForPostalCode={
                locationsErrorMessageForPostalCode
              }
              errorMessageForCountry={errorMessageForCountry}
              onCreditCardCVCError={setErrorMessageForCreditCardCVC}
              onCreditCardExpirationError={
                setErrorMessageForCreditCardExpiration
              }
              onCreditCardNumberError={setErrorMessageForCreditCardNumber}
              onValidate={() => setValidated(true)}
            />
            <CreditCardsStoreButton
              onClick={onStoreCreditCard}
              storingInProgress={storingInProgress}
              storingSuccess={storingSuccess}
              storingFailure={storingFailure}
              failureMessage={failureMessage}
            />
            <CreditCardsAddLegalease />
          </Stack>
        </CreditCardsFormPane>
      </Stack>
    </ControlPresentationProvider>
  );
}
export default ExternalAddCreditCardScreen;
